import { User } from 'types';

const userKey = 'authUser';
const darkThemeKey = 'isDarkTheme';
const rememberedEmailKey = 'rememberedEmail';
const ssoStateNonceKey = 'sso_state_nonce';
const auth0IdKey = 'auth0Id';
const authStageKey = 'authStage';
const tenantIdKey = 'tenantId';
const canSkipVideosKey = 'canSkipVideos';
const videoProgressStoreKey = 'video-progress-store-v2';
const showCourseResetKey = 'show-course-reset';
const shouldShowCourseReset =
  localStorage.getItem(showCourseResetKey) === 'true';

export interface Tokens {
  token?: string;
  refreshToken?: string;
}

export type TokenKey = 'accessToken' | 'refreshToken';
export type AuthStage = 'mfa' | 'authenticated';
export type VideoProgressEntry = {
  progress: number;
  watched: boolean;
};
export type VideoProgressStore = {
  currentSubmissionId?: string;
  resources: Record<string, Record<string, VideoProgressEntry>>;
};

const accessTokenKey: TokenKey = 'accessToken';
const refreshTokenKey: TokenKey = 'refreshToken';
const tokenKeys: TokenKey[] = [accessTokenKey, refreshTokenKey];

export const rememberEmail = (remember: boolean, email?: string) => {
  if (remember && email) {
    localStorage.setItem(rememberedEmailKey, email);
  } else {
    localStorage.removeItem(rememberedEmailKey);
  }
};

export const getRememberedEmail = () =>
  localStorage.getItem(rememberedEmailKey);

export const saveSsoStateNonce = (nonce: string) =>
  localStorage.setItem(ssoStateNonceKey, nonce);

export const getSsoStateNonce = () => localStorage.getItem(ssoStateNonceKey);

export const clearSsoStateNonce = () =>
  localStorage.removeItem(ssoStateNonceKey);

export const saveDarkTheme = (isDarkTheme?: boolean) => {
  if (typeof isDarkTheme === 'boolean') {
    localStorage.setItem(darkThemeKey, String(isDarkTheme));
  } else {
    localStorage.removeItem(darkThemeKey);
  }
};

export const getIsDarkTheme = (): boolean | undefined => {
  const isDarkTheme = localStorage.getItem(darkThemeKey);
  if (isDarkTheme === null) {
    return undefined;
  }
  return isDarkTheme === 'true';
};

export const clearTokens = () =>
  tokenKeys.forEach((tokenKey) => localStorage.removeItem(tokenKey));

export const clearUser = () => localStorage.removeItem(userKey);
export const clearTenantId = () => localStorage.removeItem(tenantIdKey);
export const clearVideoProgressStore = () =>
  localStorage.removeItem(videoProgressStoreKey);

export const clearAll = () => {
  clearTokens();
  clearUser();
  clearTenantId();
  clearVideoProgressStore();
  clearAuth0Id();
  clearAuthStage();
};

export const saveToken = (tokenKey: TokenKey, token: string) =>
  localStorage.setItem(tokenKey, token);

export const saveTokens = ({ token, refreshToken }: Tokens) => {
  if (token) {
    saveToken('accessToken', token);
  }
  if (refreshToken) {
    saveToken('refreshToken', refreshToken);
  }
};

export const getToken = (tokenKey: TokenKey = accessTokenKey) => {
  return localStorage.getItem(tokenKey) || undefined;
};

export const saveTenantId = (tenantId: string) =>
  localStorage.setItem(tenantIdKey, tenantId);

export const getTenantId = () => localStorage.getItem(tenantIdKey) || undefined;

export const saveUser = (user: User) => {
  if (user.tenantId) {
    saveTenantId(user.tenantId);
  }

  localStorage.setItem(userKey, JSON.stringify(user));
};

export const getUser = (): User | undefined => {
  if (typeof window === 'undefined') return undefined;

  const user = localStorage?.getItem(userKey);
  if (!user) {
    return undefined;
  }
  return JSON.parse(user);
};

export const saveAuth0Id = (auth0Id: string) =>
  localStorage.setItem(auth0IdKey, auth0Id);

export const getAuth0Id = () => localStorage.getItem(auth0IdKey) as string;

export const clearAuth0Id = () => localStorage.removeItem(auth0IdKey);

export const saveAuthStage = (authStage: AuthStage) =>
  localStorage.setItem(authStageKey, authStage);

export const getAuthStage = (): AuthStage | undefined => {
  const authStage = localStorage.getItem(authStageKey);

  if (authStage === 'mfa' || authStage === 'authenticated') {
    return authStage;
  }

  return undefined;
};

export const clearAuthStage = () => localStorage.removeItem(authStageKey);

export const getTokens = () => ({
  token: getToken('accessToken'),
  refreshToken: getToken('refreshToken'),
});

export const getCanSkipVideos = (): boolean => {
  return localStorage.getItem(canSkipVideosKey) === 'true';
};

export const getShouldShowCourseReset = (): boolean => shouldShowCourseReset;

export const getVideoProgressStore = (): VideoProgressStore => {
  try {
    const raw = localStorage.getItem(videoProgressStoreKey);
    if (!raw) {
      return { resources: {} };
    }
    const parsed = JSON.parse(raw) as VideoProgressStore;
    return {
      resources: parsed.resources || {},
      currentSubmissionId: parsed.currentSubmissionId,
    };
  } catch {
    return { resources: {} };
  }
};

export const setVideoProgressStore = (store: VideoProgressStore) => {
  try {
    localStorage.setItem(videoProgressStoreKey, JSON.stringify(store));
  } catch (error) {
    console.error(error);
  }
};
